import React, { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Props {
  open: boolean;
  title: string;
  /** Markdown 内容 */
  content: string;
  onClose: () => void;
}

const HelpDialog: React.FC<Props> = ({ open, title, content, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-[min(720px,92vw)] max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            aria-label="关闭"
            className="w-8 h-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center transition-colors"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto help-md">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => (
                <h1 className="text-xl font-bold text-gray-900 mt-2 mb-3">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-lg font-semibold text-teal-700 mt-5 mb-2">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">{children}</h3>
              ),
              p: ({ children }) => <p className="text-sm text-gray-700 leading-relaxed mb-3">{children}</p>,
              ul: ({ children }) => <ul className="text-sm text-gray-700 list-disc pl-6 mb-3 space-y-1">{children}</ul>,
              ol: ({ children }) => <ol className="text-sm text-gray-700 list-decimal pl-6 mb-3 space-y-1">{children}</ol>,
              li: ({ children }) => <li className="leading-relaxed">{children}</li>,
              code: ({ children, className }: any) => {
                // 块级 code 一般有 language-* 类（fenced），inline code 则没有
                const isBlock = typeof className === 'string' && className.includes('language-');
                return isBlock ? (
                  <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg my-2 font-mono text-xs overflow-x-auto whitespace-pre">
                    {children}
                  </code>
                ) : (
                  <code className="px-1.5 py-0.5 mx-0.5 rounded bg-pink-50 text-pink-700 font-mono text-[12.5px]">
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => <pre className="my-2">{children}</pre>,
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-amber-400 bg-amber-50 px-4 py-2 my-3 rounded-r text-sm text-amber-900">
                  {children}
                </blockquote>
              ),
              table: ({ children }) => (
                <div className="overflow-x-auto my-3">
                  <table className="w-full text-sm border-collapse">{children}</table>
                </div>
              ),
              thead: ({ children }) => <thead className="bg-teal-50">{children}</thead>,
              th: ({ children }) => (
                <th className="border border-teal-200 px-3 py-2 text-left text-teal-900 font-semibold">{children}</th>
              ),
              td: ({ children }) => (
                <td className="border border-gray-200 px-3 py-2 text-gray-700 align-top">{children}</td>
              ),
              strong: ({ children }) => <strong className="text-gray-900 font-semibold">{children}</strong>,
              hr: () => <hr className="my-4 border-dashed border-gray-300" />,
            }}
          >
            {content}
          </ReactMarkdown>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            知道了
          </button>
        </div>
      </div>
    </div>
  );
};

interface ButtonProps {
  title: string;
  content: string;
  /** 可选的额外 className */
  className?: string;
}

/** 内联 ? 按钮，点击展开 HelpDialog */
export const HelpButton: React.FC<ButtonProps> = ({ title, content, className = '' }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={title}
        title={title}
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold hover:bg-teal-200 transition-colors ${className}`}
      >
        ?
      </button>
      <HelpDialog open={open} title={title} content={content} onClose={() => setOpen(false)} />
    </>
  );
};

export default HelpDialog;
